import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import { useAdminUsers, AdminUser } from '@/hooks/useAdminUsers';
import { UsersTable } from '@/components/admin/UsersTable';
import { UserFormDialog } from '@/components/admin/UserFormDialog';
import { AdminSidebar, type AdminTab } from '@/components/admin/AdminSidebar';
import { AdminPortPros } from '@/components/admin/AdminPortPros';
import { AdminPorts } from '@/components/admin/AdminPorts';
import { AdminStats } from '@/components/admin/AdminStats';
import { SubscriptionsTable } from '@/components/admin/SubscriptionsTable';
import { TokenUsageStats } from '@/components/admin/TokenUsageStats';
import { SecurityPanel } from '@/components/admin/SecurityPanel';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertTriangle } from 'lucide-react';

export const AdminPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { users, loading, isAdmin, createUser, updateUser, deleteUser } = useAdminUsers();
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Rediriger si pas connecté
  if (!authLoading && !user) {
    console.log('AdminPage: Utilisateur non connecté, redirection vers /auth');
    return <Navigate to="/auth" replace />;
  }

  // Afficher le loader pendant la vérification des permissions
  if (authLoading || loading) {
    console.log('AdminPage: Chargement en cours...', { authLoading, loading, user: !!user });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nautic mx-auto"></div>
          <p className="text-muted-foreground">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  // Afficher message d'accès refusé si pas admin
  if (!isAdmin) {
    console.log('AdminPage: Accès refusé - utilisateur non admin', { user: user?.email, isAdmin });
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Accès refusé</h2>
                <p className="text-muted-foreground mt-2">
                  Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                  Seuls les administrateurs peuvent gérer les utilisateurs.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Connecté en tant que: {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (selectedUser) {
      return await updateUser(selectedUser.id, data);
    } else {
      return await createUser(data);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
  };

  return (
    <div className="flex min-h-screen">
      <AdminSidebar activeTab={activeTab} onTabChange={setActiveTab} />
      
      <main className="flex-1 p-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-nautic" />
            <span className="text-sm text-muted-foreground">Interface Administrateur</span>
          </div>
        </div>

        {activeTab === 'users' && (
          <>
            <UsersTable
              users={users}
              onEdit={handleEdit}
              onDelete={deleteUser}
              onCreate={handleCreate}
              loading={loading}
            />

            <UserFormDialog
              open={isFormOpen}
              onOpenChange={(open) => {
                setIsFormOpen(open);
                if (!open) setSelectedUser(null);
              }}
              user={selectedUser}
              onSubmit={handleFormSubmit}
            />
          </>
        )}
        
        {activeTab === 'subscriptions' && <SubscriptionsTable />}
        
        {activeTab === 'stats' && <AdminStats />}
        
        {activeTab === 'tokens' && <TokenUsageStats />}
        
        {activeTab === 'ports' && <AdminPorts />}

        {activeTab === 'pros' && <AdminPortPros />}

        {activeTab === 'security' && <SecurityPanel />}
      </main>
    </div>
  );
};